import React, { useRef, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import PageLayout from '@/components/PageLayout'
import Header from '@/components/Header'
import Back from '@/components/Back'
import CustomButton from '@/components/CustomButton'
import { APIWeb } from '@/api/index'
import { Toast } from 'react-vant'
import { toFixedNoRound } from '@/utils'

export default function SetLimit() {
    const { t } = useTranslation()
    const [loading, setLoading] = useState(false)
    const [businessLimit, setBusinessLimit] = useState({
        business_day_limit_amt: '',
        business_day_max_tran: '',
        business_per_tran_max_amt: '',
        business_per_tran_min_amt: ''
    })
    const singleLimitRef = useRef<HTMLInputElement>(null)
    const dailyLimitRef = useRef<HTMLInputElement>(null)

    const adjustInputWidth = (input: HTMLInputElement) => {
        if (!input) return
        // 创建一个临时的 span 元素来测量文本宽度
        const span = document.createElement('span')
        span.style.visibility = 'hidden'
        span.style.position = 'absolute'
        span.style.whiteSpace = 'nowrap'
        span.style.font = window.getComputedStyle(input).font
        span.textContent = input.value || input.placeholder || '0'

        document.body.appendChild(span)
        const textWidth = span.offsetWidth
        document.body.removeChild(span)

        // 设置最小宽度为 8px，最大宽度为 200px，并添加一些 padding
        const newWidth = Math.max(20, Math.min(200, textWidth + 16))
        input.style.width = `${newWidth}px`
    }

    useEffect(() => {
        // 页面进入时同步卡片限额
        const syncCardVelocity = async () => {
            Toast.loading(t('同步卡片限额中'))

            try {
                const result = await APIWeb.syncCardVelocity()
                Toast.clear()
                setBusinessLimit({
                    business_day_limit_amt: toFixedNoRound(result?.business_day_limit_amt || 0, 2),
                    business_day_max_tran: result?.business_day_max_tran?.toString() || '',
                    business_per_tran_max_amt: toFixedNoRound(result?.business_per_tran_max_amt || 0, 2),
                    business_per_tran_min_amt: toFixedNoRound(result?.business_per_tran_min_amt || 0, 2)
                })
            } catch (error) {
                console.error('同步卡片限额失败:', error)
            } finally {
                Toast.clear()
            }
        }

        syncCardVelocity()

        // 初始化宽度
        if (singleLimitRef.current) {
            adjustInputWidth(singleLimitRef.current)
        }
        if (dailyLimitRef.current) {
            adjustInputWidth(dailyLimitRef.current)
        }
    }, [])

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target
        // 限制输入长度为16个字符
        if (value.length > 16) {
            return
        }
        // 更新对应的状态
        if (name === 'business_per_tran_max_amt') {
            setBusinessLimit({ ...businessLimit, business_per_tran_max_amt: value })
        } else if (name === 'business_day_limit_amt') {
            setBusinessLimit({ ...businessLimit, business_day_limit_amt: value })
        }
        adjustInputWidth(e.target)
    }

    // 检测限额是否有效且符合业务规则
    const hasLimitChanged = () => {
        const business_per_tran_max_amt = parseFloat(businessLimit.business_per_tran_max_amt) || 0
        const business_day_limit_amt = parseFloat(businessLimit.business_day_limit_amt) || 0
        // 两个值都必须大于0才能提交变更
        const bothValuesValid = business_day_limit_amt > 0 && business_per_tran_max_amt > 0
        return bothValuesValid
    }

    // 更新卡片限额
    const updateCardLimit = async () => {
        if (!hasLimitChanged()) return

        setLoading(true)
        try {
            const params: CardUpdateLimitRequest = {}
            const singleLimitNum = Number(businessLimit.business_per_tran_max_amt)
            const dailyLimitNum = Number(businessLimit.business_day_limit_amt)
            const maxTrans = Number(businessLimit.business_day_max_tran)
            const txnMinAmt = Number(businessLimit.business_per_tran_min_amt)
            params.per_tran_limit = {
                txnMaxAmt: singleLimitNum,
                txnMinAmt
            }
            params.daily_velocity = {
                cumulativeAmt: dailyLimitNum,
                maxTrans
            }
            await APIWeb.updateCardVelocity(params)
            Toast.success(t('限额更新成功'))

        } catch (error) {
            console.error(t('更新限额失败:'), error)
        } finally {
            setLoading(false)
        }
    }

    return (
        <PageLayout>
            <Header leftChildren={<Back />} centerChildren={<span className="text-3.75 font-medium text-black">{t('限额')}</span>} />
            <div className='px-6 py-6'>
                <div className='flex justify-center my-10'>
                    <img className='w-50' src="/images/set-limit/set-limit.png" alt="icon" />
                </div>
                <div className='text-center'>
                    <div className='flex items-center justify-between px-4'>
                        <div className='text-[#000000]/50 text-xs text-left whitespace-wrap'>{t('单笔交易限额')}</div>
                        <div className='flex items-center justify-end ml-5 flex-1'>
                            <div className='border-2 border-b-solid border-[#1A1A1A] flex-1 flex items-center'>
                                <input
                                    ref={singleLimitRef}
                                    name="business_per_tran_max_amt"
                                    className='flex-1 w-full text-right text-base text-black font-bold outline-none px-2 py-1'
                                    type="number"
                                    placeholder="0"
                                    value={businessLimit.business_per_tran_max_amt}
                                    onChange={handleInputChange}
                                />
                                <span className='font-bold'> USD</span>
                            </div>
                            <img className='ml-3' src="/icon/edit.svg" alt="edit" />
                        </div>
                    </div>
                    <div className='flex items-center justify-between px-4 mt-5'>
                        <div className='text-[#000000]/50 text-xs text-left whitespace-wrap'>{t('日限额')}</div>
                        <div className='flex items-center justify-end ml-5 flex-1'>
                            <div className='border-2 border-b-solid border-[#1A1A1A] flex-1 flex items-center'>
                                <input
                                    ref={dailyLimitRef}
                                    name="business_day_limit_amt"
                                    className='flex-1 w-full text-right text-base text-black font-bold outline-none px-2 py-1'
                                    type="number"
                                    placeholder="0"
                                    value={businessLimit.business_day_limit_amt}
                                    onChange={handleInputChange}
                                />
                                <span className='font-bold'> USD</span>
                            </div>
                            <img className='ml-3' src="/icon/edit.svg" alt="edit" />
                        </div>
                    </div>
                </div>

                <div className='flex justify-center mt-10'>
                    <CustomButton
                        disabled={loading || !hasLimitChanged()}
                        loading={loading}
                        color='primary'
                        onClick={updateCardLimit}
                    >
                        {loading ? t('更新中...') : t('变更')}
                    </CustomButton>
                </div>
                <div className='text-center text-[#000000]/50 text-xs mt-5'>{t('单次最大限额')}</div>
            </div>
        </PageLayout>
    )
}